import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useEffect, useState } from "react";

/**
 * Asks the admin to pick a reason from `options` (see
 * config/application.js) before confirming an action such as declining or
 * closing an application. An option with `detail` also shows a text field,
 * mandatory when `detail.required` is set.
 *
 * `onConfirm` receives `{ reason, detail }` where `detail` is the trimmed
 * text or null.
 */
export const ReasonDialog = ({
  open,
  onClose,
  onConfirm,
  saving,
  title,
  description,
  options,
  confirmLabel,
  confirmColor = "error",
}) => {
  const [reason, setReason] = useState("");
  const [detail, setDetail] = useState("");

  useEffect(() => {
    if (open) {
      setReason("");
      setDetail("");
    }
  }, [open]);

  const selected = options.find((option) => option.value === reason);
  const detailConfig = selected?.detail;
  const trimmedDetail = detail.trim();
  const canConfirm =
    !!selected && (!detailConfig?.required || trimmedDetail.length > 0);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        <FormControl disabled={saving} fullWidth>
          <RadioGroup
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setDetail("");
            }}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        </FormControl>
        {detailConfig && (
          <TextField
            label={detailConfig.label}
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            disabled={saving}
            required={!!detailConfig.required}
            multiline
            minRows={2}
            fullWidth
            margin="normal"
            helperText="Kept internally against the application; not sent to the customer."
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color={confirmColor}
          loading={saving}
          disabled={!canConfirm}
          onClick={() =>
            onConfirm({
              reason,
              detail: detailConfig && trimmedDetail ? trimmedDetail : null,
            })
          }
        >
          {confirmLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
